import { api } from './client';
import type { AuthUser } from '@/domain/types';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  user: AuthUser;
  requiresPasswordReset: boolean;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  newPassword: string;
  resetToken?: string;
}

export const authApi = {
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>('/auth/login', data);
    return response.data;
  },

  /**
   * The signed-in user with their partner's *current* capabilities.
   *
   * The login response is cached until sign-out, so it cannot satisfy FR-053 on
   * its own — an admin changing a partner setting would need the rep to log out
   * and back in. Refetching this keeps permissions live instead.
   */
  me: async (): Promise<{ user: AuthUser }> => {
    const response = await api.get<{ user: AuthUser }>('/auth/me');
    return response.data;
  },

  logout: async (pushToken?: string): Promise<void> => {
    // Send this browser's push token so the backend removes it on logout
    // (server-side, guaranteed regardless of which client logout path ran).
    await api.post('/auth/logout', pushToken ? { pushToken } : {});
  },

  forgotPassword: async (data: ForgotPasswordRequest): Promise<void> => {
    await api.post('/auth/forgot-password', data);
  },

  resetPassword: async (data: ResetPasswordRequest): Promise<{ token: string | null }> => {
    const response = await api.post<{ success: boolean; token: string | null }>(
      '/auth/reset-password',
      data,
    );
    return { token: response.data.token ?? null };
  },
};
